import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Printer, FileDown, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const numToWords = (n) => {
  // Lightweight INR amount-in-words helper for invoices.
  if (n == null || isNaN(n)) return '';
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const inWords = (num) => {
    if (num < 20) return a[num];
    if (num < 100) return b[Math.floor(num/10)] + (num%10 ? ' ' + a[num%10] : '');
    if (num < 1000) return a[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' ' + inWords(num%100) : '');
    if (num < 100000) return inWords(Math.floor(num/1000)) + ' Thousand' + (num%1000 ? ' ' + inWords(num%1000) : '');
    if (num < 10000000) return inWords(Math.floor(num/100000)) + ' Lakh' + (num%100000 ? ' ' + inWords(num%100000) : '');
    return inWords(Math.floor(num/10000000)) + ' Crore' + (num%10000000 ? ' ' + inWords(num%10000000) : '');
  };
  const rupees = Math.floor(n);
  const paise = Math.round((n - rupees) * 100);
  let s = inWords(rupees) + ' Rupees';
  if (paise) s += ' and ' + inWords(paise) + ' Paise';
  return s + ' Only';
};

const AdminInvoicePrint = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/manual-invoices/${invoiceId}`, { withCredentials: true });
        setData(data);
        if (searchParams.get('print') === '1') {
          // small delay so the DOM is painted
          setTimeout(() => window.print(), 400);
        }
      } catch { toast.error('Failed to load invoice'); }
      finally { setLoading(false); }
    })();
  }, [invoiceId, searchParams]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!data) return null;

  const { invoice, seller } = data;

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/manual-invoices/${invoice.invoice_id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (invoice.invoice_number || invoice.invoice_id).replace(/[\\/\s]/g, '_');
      link.setAttribute('download', `tax_invoice_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const emailPdf = async () => {
    const to = window.prompt('Send tax invoice PDF to (email):', '');
    if (to == null) return;
    if (!to.trim() || !/^\S+@\S+\.\S+$/.test(to.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/api/admin/manual-invoices/${invoice.invoice_id}/email`,
        { to_email: to.trim() },
        { withCredentials: true }
      );
      toast.success(data.message || 'Invoice emailed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to email invoice'); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="bg-[#F4F4F4] min-h-screen">
      {/* Print-only CSS — hides toolbar + sets A4 layout */}
      <style>{`
        @page { size: A4; margin: 14mm; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .invoice-sheet { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
        }
        .invoice-sheet { background: #fff; max-width: 820px; margin: 0 auto; padding: 32px 36px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
        .invoice-sheet table { width: 100%; border-collapse: collapse; }
        .invoice-sheet .items th, .invoice-sheet .items td { border: 1px solid #333; padding: 6px 8px; font-size: 12px; }
        .invoice-sheet .items th { background: #f1f1f1; text-align: left; font-weight: 700; }
        .invoice-sheet .items td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .invoice-sheet .totals td { padding: 4px 8px; font-size: 12px; }
        .invoice-sheet .totals .label { text-align: right; color: #444; }
        .invoice-sheet .totals .val { text-align: right; font-weight: 700; width: 140px; }
        .invoice-sheet .grand td { background: #f1f1f1; font-weight: 800; font-size: 14px; padding: 8px; }
        .invoice-sheet h1 { font-size: 22px; font-weight: 800; letter-spacing: 1px; }
        .invoice-sheet h2 { font-size: 18px; font-weight: 800; text-align: center; letter-spacing: 2px; margin: 18px 0 12px; }
        .invoice-sheet .meta { font-size: 12px; line-height: 1.5; }
        .invoice-sheet .meta b { font-weight: 700; }
        .invoice-sheet .box { border: 1px solid #333; padding: 8px 10px; }
      `}</style>

      <div className="no-print sticky top-0 bg-white border-b border-[#E0E0E0] z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/manual-invoices')} data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to invoices
          </Button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="download-pdf-button">
              <FileDown className="h-4 w-4 mr-1" /> Save as PDF
            </Button>
            <Button size="sm" variant="outline" onClick={emailPdf} data-testid="email-pdf-button">
              <Mail className="h-4 w-4 mr-1" /> Email PDF
            </Button>
            <Button size="sm" onClick={() => window.print()} data-testid="print-button">
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
          </div>
        </div>
      </div>

      <div className="py-6 px-4">
        <div className="invoice-sheet" data-testid="invoice-sheet">
          {/* Seller header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
            <div>
              <h1>{seller.firm_name || 'IND-PETRICHOR PVT. LTD.'}</h1>
              {seller.shop_name && <div className="meta" style={{ color: '#666' }}>{seller.shop_name}</div>}
              <div className="meta" style={{ marginTop: 4 }}>{seller.address || ''}</div>
              <div className="meta">
                {seller.phone && <span>Ph: {seller.phone} </span>}
                {seller.email && <span>· {seller.email}</span>}
              </div>
              {seller.gst_number && <div className="meta"><b>GSTIN:</b> {seller.gst_number}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, letterSpacing: 1, color: '#666', textTransform: 'uppercase', fontWeight: 700 }}>{invoice.category === 'gallery' ? 'Gallery Sale' : 'Domestic Sale'}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Original for Recipient</div>
            </div>
          </div>

          <h2>TAX INVOICE</h2>

          {/* Invoice meta + Bill To */}
          <table style={{ marginBottom: 12 }}>
            <tbody>
              <tr>
                <td style={{ verticalAlign: 'top', width: '50%', paddingRight: 8 }}>
                  <div className="box">
                    <div className="meta" style={{ fontWeight: 700, marginBottom: 4 }}>Bill To</div>
                    <div className="meta" style={{ fontSize: 13, fontWeight: 700 }}>{invoice.party?.name || '—'}</div>
                    {invoice.party?.address && <div className="meta">{invoice.party.address}</div>}
                    {invoice.party?.gstin && <div className="meta"><b>GSTIN:</b> {invoice.party.gstin}</div>}
                  </div>
                </td>
                <td style={{ verticalAlign: 'top', width: '50%' }}>
                  <div className="box">
                    <table>
                      <tbody>
                        <tr><td className="meta" style={{ paddingRight: 8 }}><b>Invoice No.</b></td><td className="meta">{invoice.invoice_number}</td></tr>
                        <tr><td className="meta" style={{ paddingRight: 8 }}><b>Date</b></td><td className="meta">{fmtDate(invoice.date)}</td></tr>
                        <tr><td className="meta" style={{ paddingRight: 8 }}><b>Category</b></td><td className="meta" style={{ textTransform: 'capitalize' }}>{invoice.category}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Items */}
          <table className="items">
            <thead>
              <tr>
                <th style={{ width: 30 }}>#</th>
                <th>Description</th>
                <th style={{ width: 80 }}>HSN</th>
                <th style={{ width: 50, textAlign: 'right' }}>Qty</th>
                <th style={{ width: 80, textAlign: 'right' }}>Rate</th>
                <th style={{ width: 56, textAlign: 'right' }}>Disc %</th>
                <th style={{ width: 56, textAlign: 'right' }}>GST %</th>
                <th style={{ width: 90, textAlign: 'right' }}>GST Amt</th>
                <th style={{ width: 100, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((it, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{it.description}</td>
                  <td>{it.hsn || '—'}</td>
                  <td className="num">{Number(it.quantity).toFixed(2)}</td>
                  <td className="num">{'\u20B9'}{Number(it.rate).toFixed(2)}</td>
                  <td className="num">{Number(it.discount_percent || 0).toFixed(2)}%</td>
                  <td className="num">{Number(it.gst_rate).toFixed(2)}%</td>
                  <td className="num">{'\u20B9'}{Number(it.gst_amount).toFixed(2)}</td>
                  <td className="num">{'\u20B9'}{Number(it.amount).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <table className="totals" style={{ marginTop: 8 }}>
            <tbody>
              <tr>
                <td style={{ width: '60%', verticalAlign: 'top' }}>
                  <div className="meta"><b>Amount in words:</b> {numToWords(invoice.total)}</div>
                  {invoice.notes && <div className="meta" style={{ marginTop: 8 }}><b>Notes:</b> {invoice.notes}</div>}
                </td>
                <td style={{ width: '40%' }}>
                  <table>
                    <tbody>
                      <tr><td className="label">Subtotal (Taxable)</td><td className="val">{'\u20B9'}{Number(invoice.subtotal || 0).toFixed(2)}</td></tr>
                      <tr><td className="label">Total GST</td><td className="val">{'\u20B9'}{Number(invoice.total_gst || 0).toFixed(2)}</td></tr>
                      <tr className="grand"><td className="label">Grand Total</td><td className="val">{'\u20B9'}{Number(invoice.total || 0).toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Footer */}
          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'space-between' }}>
            <div className="meta" style={{ maxWidth: '55%', color: '#555' }}>
              <div data-testid="invoice-declaration">
                <b>Declaration:</b> {seller.declaration_text || 'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.'}
              </div>
              {seller.jurisdiction_text && (
                <div className="meta" style={{ marginTop: 4, fontStyle: 'italic', color: '#777' }} data-testid="invoice-jurisdiction">
                  {seller.jurisdiction_text}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', minWidth: 180 }}>
              <div style={{ height: 50 }} />
              <div className="meta" style={{ borderTop: '1px solid #333', paddingTop: 4, fontWeight: 700 }}>
                For {seller.firm_name || 'IND-PETRICHOR PVT. LTD.'}
              </div>
              <div className="meta" style={{ color: '#666' }}>Authorised Signatory</div>
            </div>
          </div>

          {seller.website && (
            <div style={{ textAlign: 'center', marginTop: 18 }} data-testid="invoice-qr-block">
              <img src="/ind-petrichor-qr.png" width="120" height="120" alt="QR Code" />
              <div className="meta" style={{ color: '#666', marginTop: 4, fontSize: 10, wordBreak: 'break-all' }}>{seller.website}</div>
            </div>
          )}

          {seller.footer_text && (
            <div className="meta" style={{ textAlign: 'center', marginTop: 18, color: '#666', fontStyle: 'italic' }}>
              {seller.footer_text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInvoicePrint;
